import { Form, Input, InputRef } from 'antd';
import Box from 'components/Box';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import Modal from 'components/Modal';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListApis from 'features/ticketList/services/apis';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { useEffect, useMemo, useRef } from 'react';
import { getMessageError, IResponseDataBody } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import toast from 'utils/toast';
import TablePartialVoid from '../PartialVoidAllocationModal/TablePartialVoid';
import { NumberPadDecimalV2, NumberPadIntV2 } from 'components/NumberKeyBoard/Currency/NumberPadV2';
import PartialVoidAllocationModal, { PartialVoidAllocationModalRef } from '../PartialVoidAllocationModal';

type Props = {
  visible?: boolean;
  onClose: () => void;
  staffId?: string;
  partialVoid: boolean;
};

const REASONS = ['Double charge', 'Mistake', 'Training'];

interface IPartialVoidFormValues {
  amount: number;
  reason: string;
  billId: string;
  staffId?: string;
}

const PartialVoidModal = ({ visible, onClose, staffId, partialVoid }: Props) => {
  const inputDiscountValueRef = useRef<InputRef>(null);
  const ticketDetails = ticketListSelectors.ticketDetailsData.data();
  const [form] = Form.useForm<IPartialVoidFormValues>();
  const dispatch = useAppDispatch();
  const usePartialVoidAllocationRef = useRef<PartialVoidAllocationModalRef>(null);

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const handleSetReason = (val: string) => {
    form.setFieldValue('reason', val);
    form.validateFields();
  };

  const handleSubmit = async (value: IPartialVoidFormValues) => {
    try {
      const body = {
        billId: value.billId,
        reason: value.reason,
        staffId: value.staffId || '',
        amount: value.amount,
      };

      let res: IResponseDataBody;
      if (partialVoid) {
        res = await ticketListApis.partialVoidTicket(body);
      } else {
        res = await ticketListApis.refundByBillId(body);
      }
      if (res?.data?.data) {
        usePartialVoidAllocationRef.current?.open(value.amount);
        dispatch(ticketListActions.getTicketDetails.fetch(value.billId));
        handleClose();
      }
    } catch (error) {
      // toast.error(getMessageError(error) || 'Failed to partial void ticket');
    }
  };

  useEffect(() => {
    if (!ticketDetails || !visible) return;

    form.setFieldsValue({
      billId: ticketDetails.billId,
      staffId: staffId,
      amount: 0,
    });
  }, [ticketDetails, visible, staffId]);


  return (
    <>
    <StyledModal
      visible={visible}
      modalTitle={partialVoid ? 'Partial Void' : 'Refund'}
      noneFooterStyle
      onClose={handleClose}
      onSubmit={() => form.submit()}
      v2
      noneBodyStyle
      width={'1000px'}
      closable={false}
      okTitle={'OK'}
      destroyOnClose
    >
      <Wrapper className='v2' form={form} validateTrigger={['onSubmit']} onFinish={handleSubmit} layout='vertical'>
        <Form.Item name='items' hidden>
          <Input />
        </Form.Item>
        <Form.Item name='billId' hidden>
          <Input />
        </Form.Item>
        <Form.Item name='staffId' hidden>
          <Input />
        </Form.Item>
        <AmountBox>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue, getFieldError }) => {
              const amount = getFieldValue('amount');
              const getError = getFieldError('amount');
              return (
                <Box display='flex' flexDirection='column' alignItems='center' gap='2'>
                  <CurrencyInputPrecision size='large' value={amount} readOnly />
                  {getError && (
                    <Text variant='H8' color='v2_other_3' style={{ fontWeight: 500, textAlign: 'center' }}>
                      {getError}
                    </Text>
                  )}
                </Box>
              );
            }}
          </Form.Item>
          <OriginalPrice>
            <Box display='flex' alignItems='center' gap='2' pr='4' style={{ borderRight: '1px solid #CBD5E1' }}>
              <Text variant='H8' color='text_3_v2' style={{ fontWeight: 500 }}>
                Amount:
              </Text>
              <Text variant='H9'>${ticketDetails?.total?.toFixed(2) || '0.00'}</Text>
            </Box>
            <Box display='flex' alignItems='center' gap='2'>
              <Text variant='H8' color='text_3_v2' style={{ fontWeight: 500 }}>
                Remaining:
              </Text>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const amount = getFieldValue('amount');
                  return (
                    <Text variant='H9'>
                      ${ticketDetails?.total && amount ? (ticketDetails.total - parseFloat(amount)).toFixed(2) : '0.00'}
                    </Text>
                  );
                }}
              </Form.Item>
            </Box>
          </OriginalPrice>
        </AmountBox>
        <Content>
          <ReasonBox>
            <Form.Item
              name='reason'
              label='Reason'
              // rules={[{ required: true, message: 'Please input the reason for void!' }]}
            >
              <Input.TextArea placeholder='Input reason for void' autoSize={{ minRows: 3, maxRows: 5 }} />
            </Form.Item>
            <RecommendedReason>
              {REASONS.map((item) => (
                <RecommendedReasonItem key={item} onClick={() => handleSetReason(item)}>
                  <Text variant='CONTENT_2' color='v2_blue_3' style={{ fontWeight: 500 }}>
                    {item}
                  </Text>
                </RecommendedReasonItem>
              ))}
            </RecommendedReason>
          </ReasonBox>
          <Form.Item
            name='amount'
            rules={[
              {
                required: true,
                message: 'Please input the amount to void!',
              },
              {
                validator: (_, value) => {
                  const amount = Number(value || 0);

                  if (amount <= 0) {
                    return Promise.reject(new Error('Amount must be greater than 0'));
                  }

                  if (amount > (ticketDetails?.total || 0)) {
                    return Promise.reject(new Error('Amount cannot exceed the original ticket total'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
            noStyle
          >
            <NumberPadDecimalV2 controlRef={inputDiscountValueRef} />
          </Form.Item>
        </Content>
      </Wrapper>
    </StyledModal>
    <PartialVoidAllocationModal ref={usePartialVoidAllocationRef} />
    </>
  );
};

export default PartialVoidModal;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    height: 100%;
    overflow-y: hidden !important;
  }
`;

const Wrapper = styled(Form<IPartialVoidFormValues>)`
  display: flex;
  flex-direction: column;
  font-family: Poppins !important;

  .ant-form-item {
    margin-bottom: 0;
  }

  .NumberPadStyled {
    padding: 0 !important;
  }
`;

const OriginalPrice = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 0;
  gap: 24px;
  background: ${colorTheme.v2_neutral_2};
`;

const AmountBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px;

  .ant-form-item-label {
    text-align: center;

    > label {
      font-family: Poppins;
      font-size: 20px;
    }
  }

  .ant-input {
    height: 5rem !important;
    font-size: 40px;
    text-align: center;
    font-family: Poppins;
    font-weight: 500;
    border: none;
  }
`;

const ReasonBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ant-form-item-label > label {
      font-family: Poppins;
      font-size: 20px;
    }
  }

  .ant-input {
    font-family: Poppins;
    font-weight: 500;
  }
`;

const RecommendedReason = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
`;

const RecommendedReasonItem = styled.div`
  display: flex;
  height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 100px;
  border: 1px solid #2563eb;
  background: #eef4ff;
  box-sizing: border-box;
  cursor: pointer;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  gap: 16px;
  background: #f1f5f9;

  .clear {
    background: #c6e9ef !important;
  }
`;
